import {
  assertPOSDomainAdapter,
} from "../POSDomainAdapter";


export class POSDomainRegistryError extends Error {
  constructor(
    message,
    {
      code = "POS_DOMAIN_REGISTRY_ERROR",
      details = {},
    } = {}
  ) {
    super(message);

    this.name = "POSDomainRegistryError";
    this.code = code;

    this.details = Object.freeze({
      ...details,
    });
  }
}


/**
 * Creates one isolated POS domain registry instance.
 *
 * Each registry owns its private Map.
 *
 * The registry:
 * - validates POSDomainAdapter contracts;
 * - indexes adapters by domainId;
 * - performs no activity resolution;
 * - performs no publication resolution;
 * - contains no domain fallback;
 * - maintains no global state.
 */
export function createPOSDomainRegistry({
  adapters = [],
} = {}) {
  if (!Array.isArray(adapters)) {
    throw new POSDomainRegistryError(
      "adapters must be an array.",
      {
        code: "INVALID_INITIAL_ADAPTERS",
      }
    );
  }

  const registrations = new Map();


  function registerPOSDomain(adapter) {
    assertPOSDomainAdapter(adapter);

    const domainId =
      normalizeDomainId(
        adapter.getDomainId()
      );

    if (registrations.has(domainId)) {
      throw new POSDomainRegistryError(
        `POS domain already registered for domainId "${domainId}".`,
        {
          code: "DUPLICATE_DOMAIN_ID",
          details: {
            domainId,
          },
        }
      );
    }

    registrations.set(
      domainId,
      adapter
    );

    return adapter;
  }


  function unregisterPOSDomain(domainId) {
    const normalizedDomainId =
      normalizeDomainId(domainId);

    if (!registrations.has(normalizedDomainId)) {
      return false;
    }

    registrations.delete(
      normalizedDomainId
    );

    return true;
  }


  function resolvePOSDomain(domainId) {
    const normalizedDomainId =
      normalizeDomainId(domainId);

    if (!registrations.has(normalizedDomainId)) {
      throw new POSDomainRegistryError(
        `No POS domain is registered for domainId "${normalizedDomainId}".`,
        {
          code: "UNKNOWN_DOMAIN_ID",
          details: {
            domainId: normalizedDomainId,
          },
        }
      );
    }

    return registrations.get(
      normalizedDomainId
    );
  }


  function hasPOSDomain(domainId) {
    const normalizedDomainId =
      normalizeDomainId(domainId);

    return registrations.has(
      normalizedDomainId
    );
  }


  function listPOSDomains() {
    return Object.freeze(
      [...registrations.entries()]
        .sort(
          ([leftDomainId], [rightDomainId]) =>
            leftDomainId.localeCompare(
              rightDomainId
            )
        )
        .map(([, adapter]) => adapter)
    );
  }


  function clear() {
    const removedCount =
      registrations.size;

    registrations.clear();

    return removedCount;
  }


  adapters.forEach(
    registerPOSDomain
  );


  return Object.freeze({
    registerPOSDomain,
    unregisterPOSDomain,
    resolvePOSDomain,
    hasPOSDomain,
    listPOSDomains,
    clear,
  });
}


function normalizeDomainId(domainId) {
  if (
    typeof domainId !== "string"
    || domainId.trim() === ""
  ) {
    throw new POSDomainRegistryError(
      "domainId must be a non-empty string.",
      {
        code: "INVALID_DOMAIN_ID",
        details: {
          domainId,
        },
      }
    );
  }

  return domainId.trim();
}
